import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
    ArrowLeft,
    HelpCircle,
    Search,
    Target,
    Layout,
    BarChart3,
    User,
    CreditCard,
    Settings,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Mail,
    MessageSquare,
    BookOpen,
} from 'lucide-react';
import clsx from 'clsx';

// Help categories with articles
const helpCategories = [
    {
        id: 'getting-started',
        icon: Target,
        color: 'success',
        titleKey: 'helpCenter.categories.gettingStarted.title',
        articles: [
            { titleKey: 'helpCenter.categories.gettingStarted.articles.create-account', content: 'gettingStarted.createAccount' },
            { titleKey: 'helpCenter.categories.gettingStarted.articles.first-objective', content: 'gettingStarted.firstObjective' },
            { titleKey: 'helpCenter.categories.gettingStarted.articles.first-board', content: 'gettingStarted.firstBoard' },
            { titleKey: 'helpCenter.categories.gettingStarted.articles.link-task', content: 'gettingStarted.linkTask' },
        ],
    },
    {
        id: 'okr',
        icon: Target,
        color: 'primary',
        titleKey: 'helpCenter.categories.okr.title',
        articles: [
            { titleKey: 'helpCenter.categories.okr.articles.what-is-okr', content: 'okr.whatIsOkr' },
            { titleKey: 'helpCenter.categories.okr.articles.create-objective', content: 'okr.createObjective' },
            { titleKey: 'helpCenter.categories.okr.articles.add-key-result', content: 'okr.addKeyResult' },
            { titleKey: 'helpCenter.categories.okr.articles.check-in', content: 'okr.checkIn' },
        ],
    },
    {
        id: 'boards',
        icon: Layout,
        color: 'secondary',
        titleKey: 'helpCenter.categories.boards.title',
        articles: [
            { titleKey: 'helpCenter.categories.boards.articles.create-board', content: 'boards.createBoard' },
            { titleKey: 'helpCenter.categories.boards.articles.add-columns', content: 'boards.addColumns' },
            { titleKey: 'helpCenter.categories.boards.articles.create-tasks', content: 'boards.createTasks' },
            { titleKey: 'helpCenter.categories.boards.articles.move-tasks', content: 'boards.moveTasks' },
        ],
    },
    {
        id: 'analytics',
        icon: BarChart3,
        color: 'warning',
        titleKey: 'helpCenter.categories.analytics.title',
        articles: [
            { titleKey: 'helpCenter.categories.analytics.articles.dashboard', content: 'analytics.dashboard' },
            { titleKey: 'helpCenter.categories.analytics.articles.drift-detection', content: 'analytics.driftDetection' },
            { titleKey: 'helpCenter.categories.analytics.articles.progress-tracking', content: 'analytics.progressTracking' },
        ],
    },
    {
        id: 'account',
        icon: User,
        color: 'info',
        titleKey: 'helpCenter.categories.account.title',
        articles: [
            { titleKey: 'helpCenter.categories.account.articles.change-password', content: 'account.changePassword' },
            { titleKey: 'helpCenter.categories.account.articles.enable-2fa', content: 'account.enable2fa' },
            { titleKey: 'helpCenter.categories.account.articles.delete-account', content: 'account.deleteAccount' },
        ],
    },
    {
        id: 'billing',
        icon: CreditCard,
        color: 'success',
        titleKey: 'helpCenter.categories.billing.title',
        articles: [
            { titleKey: 'helpCenter.categories.billing.articles.plans', content: 'billing.plans' },
            { titleKey: 'helpCenter.categories.billing.articles.upgrade', content: 'billing.upgrade' },
            { titleKey: 'helpCenter.categories.billing.articles.refund', content: 'billing.refund' },
        ],
    },
];

// Popular articles
const popularArticles = [
    'helpCenter.popular.what-is-okr',
    'helpCenter.popular.link-task-to-kr',
    'helpCenter.popular.check-in-progress',
    'helpCenter.popular.invite-team',
    'helpCenter.popular.export-data',
];

export default function HelpCenterPage() {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategory, setExpandedCategory] = useState<string | null>('getting-started');

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col">
            {/* Header */}
            <header className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div className="flex items-center gap-2">
                            <HelpCircle className="w-5 h-5 text-primary-600" />
                            <h1 className="text-xl font-bold">{t('helpCenter.title', 'Trung tâm Hỗ trợ')}</h1>
                        </div>
                    </div>
                    <Link to="/login" className="btn-primary text-sm">
                        {t('auth.login')}
                    </Link>
                </div>
            </header>

            {/* Hero with Search */}
            <section className="bg-gradient-to-br from-primary-600 to-secondary-600 py-12">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                        {t('helpCenter.hero.title', 'Chúng tôi có thể giúp gì cho bạn?')}
                    </h2>
                    <p className="text-white/80 mb-6">
                        {t('helpCenter.hero.subtitle', 'Tìm kiếm câu trả lời hoặc duyệt qua các chủ đề hỗ trợ')}
                    </p>

                    {/* Search Bar */}
                    <div className="relative max-w-xl mx-auto">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('helpCenter.search.placeholder', 'Tìm kiếm bài viết hỗ trợ...')}
                            className="w-full pl-12 pr-4 py-4 rounded-2xl border-0 shadow-lg text-lg focus:ring-2 focus:ring-white/50"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full">
                {/* Quick Links */}
                <section className="grid sm:grid-cols-3 gap-4 mb-8">
                    <Link
                        to="/docs"
                        className="flex items-center gap-3 p-4 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-primary-400 transition-colors"
                    >
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-600/20 rounded-lg flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                            <p className="font-medium">{t('helpCenter.quickLinks.docs', 'Hướng dẫn sử dụng')}</p>
                            <p className="text-sm text-neutral-500">{t('helpCenter.quickLinks.docsDesc', 'Bắt đầu nhanh')}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-neutral-400 ml-auto" />
                    </Link>

                    <Link
                        to="/contact"
                        className="flex items-center gap-3 p-4 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-primary-400 transition-colors"
                    >
                        <div className="w-10 h-10 bg-success-100 dark:bg-success-600/20 rounded-lg flex items-center justify-center">
                            <Mail className="w-5 h-5 text-success-600" />
                        </div>
                        <div>
                            <p className="font-medium">{t('helpCenter.quickLinks.contact', 'Liên hệ hỗ trợ')}</p>
                            <p className="text-sm text-neutral-500">{t('helpCenter.quickLinks.contactDesc', 'Gửi yêu cầu')}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-neutral-400 ml-auto" />
                    </Link>

                    <a
                        href="mailto:support@1vision.work"
                        className="flex items-center gap-3 p-4 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-primary-400 transition-colors"
                    >
                        <div className="w-10 h-10 bg-warning-100 dark:bg-warning-600/20 rounded-lg flex items-center justify-center">
                            <MessageSquare className="w-5 h-5 text-warning-600" />
                        </div>
                        <div>
                            <p className="font-medium">{t('helpCenter.quickLinks.email', 'Email trực tiếp')}</p>
                            <p className="text-sm text-neutral-500">support@1vision.work</p>
                        </div>
                        <ExternalLink className="w-5 h-5 text-neutral-400 ml-auto" />
                    </a>
                </section>

                <div className="grid md:grid-cols-3 gap-8">
                    {/* Categories */}
                    <div className="md:col-span-2 space-y-4">
                        <h3 className="text-lg font-semibold mb-4">
                            {t('helpCenter.browse.title', 'Duyệt theo chủ đề')}
                        </h3>

                        {helpCategories.map((category) => {
                            const Icon = category.icon;
                            const isExpanded = expandedCategory === category.id;

                            return (
                                <div
                                    key={category.id}
                                    className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden"
                                >
                                    <button
                                        onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                                        className="w-full p-4 flex items-center gap-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
                                    >
                                        <div className={clsx(
                                            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                                            category.color === 'success' && 'bg-success-100 dark:bg-success-600/20',
                                            category.color === 'primary' && 'bg-primary-100 dark:bg-primary-600/20',
                                            category.color === 'secondary' && 'bg-secondary-100 dark:bg-secondary-600/20',
                                            category.color === 'warning' && 'bg-warning-100 dark:bg-warning-600/20',
                                            category.color === 'info' && 'bg-blue-100 dark:bg-blue-600/20',
                                        )}>
                                            <Icon className={clsx(
                                                'w-5 h-5',
                                                category.color === 'success' && 'text-success-600',
                                                category.color === 'primary' && 'text-primary-600',
                                                category.color === 'secondary' && 'text-secondary-600',
                                                category.color === 'warning' && 'text-warning-600',
                                                category.color === 'info' && 'text-blue-600',
                                            )} />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <p className="font-medium">
                                                {t(category.titleKey, category.id)}
                                            </p>
                                            <p className="text-sm text-neutral-500">
                                                {category.articles.length} {t('helpCenter.articles', 'bài viết')}
                                            </p>
                                        </div>
                                        <ChevronDown className={clsx(
                                            'w-5 h-5 text-neutral-400 transition-transform',
                                            isExpanded && 'rotate-180'
                                        )} />
                                    </button>

                                    {isExpanded && (
                                        <div className="border-t border-neutral-200 dark:border-neutral-700">
                                            {category.articles.map((article, idx) => (
                                                <button
                                                    key={idx}
                                                    className="w-full px-4 py-3 pl-16 flex items-center gap-2 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 text-left text-sm transition-colors"
                                                >
                                                    <span className="text-neutral-600 dark:text-neutral-400">
                                                        {t(article.titleKey, article.content)}
                                                    </span>
                                                    <ChevronRight className="w-4 h-4 text-neutral-400 ml-auto" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Popular Articles */}
                        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
                            <h3 className="font-semibold mb-4 flex items-center gap-2">
                                <HelpCircle className="w-5 h-5 text-primary-600" />
                                {t('helpCenter.popular.title', 'Bài viết phổ biến')}
                            </h3>
                            <div className="space-y-3">
                                {popularArticles.map((article, idx) => (
                                    <button
                                        key={idx}
                                        className="w-full text-left text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors flex items-start gap-2"
                                    >
                                        <span className="text-primary-600">•</span>
                                        {t(article, `Article ${idx + 1}`)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Need More Help */}
                        <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl p-5 text-white">
                            <h3 className="font-semibold mb-2">
                                {t('helpCenter.needHelp.title', 'Cần thêm hỗ trợ?')}
                            </h3>
                            <p className="text-sm text-white/80 mb-4">
                                {t('helpCenter.needHelp.desc', 'Đội ngũ hỗ trợ sẵn sàng giúp đỡ bạn')}
                            </p>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-white text-primary-600 px-4 py-2 rounded-lg font-medium text-sm hover:bg-white/90 transition-colors"
                            >
                                {t('helpCenter.needHelp.cta', 'Liên hệ ngay')}
                                <ChevronRight className="w-4 h-4" />
                            </Link>
                        </div>

                        {/* Status */}
                        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Settings className="w-5 h-5 text-neutral-500" />
                                {t('helpCenter.status.title', 'Trạng thái Hệ thống')}
                            </h3>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-success-600 font-medium">
                                    {t('helpCenter.status.operational', 'Tất cả hệ thống hoạt động bình thường')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-4 bg-neutral-100 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 mt-auto">
                <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                            <Target className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-neutral-900 dark:text-white">VisionBoard</span>
                    </Link>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                        <Link to="/terms" className="hover:text-primary-600">Điều khoản</Link>
                        <Link to="/privacy" className="hover:text-primary-600">Bảo mật</Link>
                        <Link to="/contact" className="hover:text-primary-600">Liên hệ</Link>
                    </div>
                    <p className="text-sm text-neutral-500">© 2025 VisionBoard</p>
                </div>
            </footer>
        </div>
    );
}
